import * as React from 'react'
import type { ReactNode } from 'react'
import { Eye, Maximize2, Minimize2, Pencil } from 'lucide-react'

import { cn } from '@/lib/utils'
import { AccessLock } from '@/components/AccessLock'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardTitle } from '@/components/ui/card'
import {
  useDutyDetailArchived,
  useDutyDetailLiveContentReadOnly,
} from '@/features/duties/components/detail/DutyDetailContexts'
import { useFetchErrorToast } from '@/features/duties/hooks/useFetchErrorToast'
import { useMountEffect } from '@/hooks/useMountEffect'

/**
 * v3 duty-detail card chrome — compound component (Figma 1378-20556 / 1288-20435).
 *
 * Compose only the parts a card needs:
 *
 *   <DutyCard>
 *     <DutyCardHeader icon={<CalendarCog />} title="Connection"
 *       expanded={isExpanded} onToggleExpanded={toggle} />
 *     <DutyCardBody>
 *       <DutyCardContent>
 *         {isError && <DutyCardError>{message}</DutyCardError>}
 *         {isExpanded ? <EditView /> : <ViewMode />}
 *       </DutyCardContent>
 *       <DutyCardEditFooter onCancel={…} onSave={…} isDirty={…} isSaving={…} />
 *     </DutyCardBody>
 *   </DutyCard>
 *
 * Two states: collapsed shows a pencil "edit" affordance; expanded shows
 * minimize + the opt-in Save/Cancel footer. The shared chrome for every duty
 * detail card.
 *
 * Cards with an edit footer wrap content + footer in `DutyCardBody`; footer-less
 * cards put `DutyCardContent` directly under `DutyCard`.
 */
type DutyCardContextValue = {
  /** id of the DutyCardContent region, wired to the header toggle's aria-controls. */
  contentId: string
}

const DutyCardContext = React.createContext<DutyCardContextValue | undefined>(
  undefined
)

/** Icon-swap lane: the outgoing glyph shrinks + blurs out as the other lands. */
const TOGGLE_ICON =
  'col-start-1 row-start-1 scale-100 transition-[opacity,filter,scale] duration-fast ease-in-out'
const TOGGLE_ICON_HIDDEN = 'scale-25 opacity-0 blur-[2px]'

export function DutyCard({ className, ...props }: React.ComponentProps<'div'>) {
  const contentId = React.useId()
  return (
    <DutyCardContext.Provider value={{ contentId }}>
      {/* shrink-0 goes with the min-height, not separately: an explicit
          min-height replaces the `auto` value that stops a flex item shrinking
          past its own content, so without it a card in a height-constrained
          column compresses to the floor and its body renders over whatever
          follows. Cards are only ever laid out in columns — the Calling page's
          row at @3xl holds column wrappers, not cards — so this never narrows
          one.
          Shared min-height so collapsed duty cards line up at a consistent
          floor; taller content (expanded cards, TestCall controls) grows past
          it. The floor also reaches the Evals case-detail cards that reuse
          DutyCard — their loaded content clears 90px (no-op) and the skeleton
          just sits at the floor, both intentional. */}
      <Card
        className={cn(
          'min-h-[90px] shrink-0 gap-3 rounded-xl border border-(--neutral-ui-border) p-3 shadow-sm',
          className
        )}
        {...props}
      />
    </DutyCardContext.Provider>
  )
}

export type DutyCardHeaderProps = {
  title: ReactNode
  /** Leading icon (rendered at 20px). */
  icon?: ReactNode
  /** Whether the card is in edit (expanded) mode. */
  expanded?: boolean
  /** Toggle handler. Omit to hide the edit/collapse affordance. */
  onToggleExpanded?: () => void
  /** aria-label for the toggle in collapsed (view) mode. */
  expandLabel?: string
  /** aria-label for the toggle in expanded (edit) mode. */
  collapseLabel?: string
  /**
   * Toggle affordance. 'edit' (default) shows a pencil/minimize for editable
   * cards; 'expand' shows maximize/minimize for view-only cards that just
   * reveal content; 'view' shows an eye/minimize for branch read-only content
   * that reviewers can still expand.
   */
  toggleVariant?: 'edit' | 'expand' | 'view'
  /** Optional extra control rendered left of the toggle. */
  action?: ReactNode
  /** Show a lock beside the title for cards restricted to elevated-access roles. */
  restricted?: boolean
  className?: string
}

export function DutyCardHeader({
  title,
  icon,
  expanded,
  onToggleExpanded,
  expandLabel,
  collapseLabel,
  toggleVariant = 'edit',
  action,
  restricted,
  className,
}: DutyCardHeaderProps) {
  const card = React.useContext(DutyCardContext)
  const liveContentReadOnly = useDutyDetailLiveContentReadOnly()
  const ExpandIcon = liveContentReadOnly
    ? Eye
    : toggleVariant === 'expand'
      ? Maximize2
      : toggleVariant === 'view'
        ? Eye
        : Pencil
  const CollapseIcon = Minimize2
  return (
    <div
      data-slot="duty-card-header"
      className={cn('flex h-8 w-full items-center gap-2 pl-1', className)}>
      {icon && (
        <span className="flex size-5 shrink-0 items-center justify-center text-primary-high-contrast [&_svg]:size-5">
          {icon}
        </span>
      )}
      <CardTitle className="min-w-0 flex-1 truncate text-sm font-medium text-primary-high-contrast">
        {title}
      </CardTitle>
      {action}
      {restricted && <AccessLock />}
      {onToggleExpanded && (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={onToggleExpanded}
          aria-expanded={expanded}
          aria-controls={card?.contentId}
          aria-label={
            expanded ? (collapseLabel ?? 'Collapse') : (expandLabel ?? 'Edit')
          }>
          {/* Both glyphs stay mounted in one grid cell so read↔edit reads as
              a swap rather than a pop. */}
          <span className="grid">
            <ExpandIcon
              className={cn(TOGGLE_ICON, expanded && TOGGLE_ICON_HIDDEN)}
            />
            <CollapseIcon
              className={cn(TOGGLE_ICON, !expanded && TOGGLE_ICON_HIDDEN)}
            />
          </span>
        </Button>
      )}
    </div>
  )
}

/**
 * Replays an enter animation whenever `state` changes — used for a card's
 * read↔edit body swap. `state` is the key, so changing it remounts the
 * wrapper and the incoming branch animates in, both directions.
 *
 * Pass the parent's layout classes when the branches are a fragment, so the
 * extra element doesn't collapse a flex column.
 */
export function DutyCardSwap({
  state,
  className,
  ...props
}: { state: string } & React.ComponentProps<'div'>) {
  return (
    <div
      key={state}
      className={cn(
        'animate-in fade-in-0 slide-in-from-top-1 duration-fast ease-smooth-out',
        className
      )}
      {...props}
    />
  )
}

export function DutyCardContent({
  className,
  id,
  ...props
}: React.ComponentProps<'div'>) {
  const card = React.useContext(DutyCardContext)
  return (
    <CardContent
      id={id ?? card?.contentId}
      className={cn('flex flex-col gap-4 px-0', className)}
      {...props}
    />
  )
}

/** Sticky-footer containing block: clamps the footer to the body, below the header. */
export function DutyCardBody({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="duty-card-body"
      className={cn('flex flex-col', className)}
      {...props}
    />
  )
}

export function DutyCardFooter({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return <CardFooter className={cn('gap-1 px-0', className)} {...props} />
}

export type DutyCardEditFooterProps = {
  onCancel: () => void
  onSave: () => void
  /**
   * The caller's own reason the content cannot be edited — for a duty card,
   * a branch other than the editable one. Archived is read from context here,
   * because it is a fact about the duty rather than about the view.
   */
  readOnly?: boolean
  /**
   * Collapse the card. When provided, Cancel exits edit mode after discarding —
   * giving a one-click way out even with nothing to save (Save stays disabled
   * while pristine, but Cancel doubles as the collapse affordance).
   */
  onCollapse?: () => void
  isDirty?: boolean
  isSaving?: boolean
  saveLabel?: string
  /**
   * Optional left-aligned control (e.g. a destructive Clear). When present the
   * footer switches from right-aligned to space-between; otherwise unchanged.
   */
  startSlot?: ReactNode
}

/**
 * Right-aligned Cancel/Save footer (Figma 1379-22486). Sticks to the viewport
 * bottom while scrolling a tall card; must be inside `DutyCardBody` so it clamps
 * below the header. Renders nothing when the content cannot be edited — an
 * archived duty, or any reason the caller passes as `readOnly`.
 *
 * Cancel stays enabled whenever a save isn't in flight (even with no pending
 * edits) so it can also collapse the card via `onCollapse`; only Save gates on
 * `isDirty`.
 */
export function DutyCardEditFooter({
  onCancel,
  onSave,
  readOnly,
  onCollapse,
  isDirty,
  isSaving,
  saveLabel = 'Save',
  startSlot,
}: DutyCardEditFooterProps) {
  const isDutyArchived = useDutyDetailArchived()
  const liveContentReadOnly = useDutyDetailLiveContentReadOnly()
  if (isDutyArchived || liveContentReadOnly || readOnly) return null

  return (
    <DutyCardFooter
      className={cn(
        'sticky bottom-0 z-10 mt-4 items-center bg-card py-3 px-6 shadow-[0_-2px_6px_-2px_rgba(0,0,0,0.08)] -mx-3',
        'animate-in fade-in-0 slide-in-from-bottom-2 duration-fast ease-smooth-out',
        startSlot ? 'justify-between' : 'justify-end'
      )}>
      {startSlot}
      <div className="flex w-72.5 max-w-full gap-1">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="flex-1"
          disabled={isSaving}
          onClick={() => {
            onCancel()
            onCollapse?.()
          }}>
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          className="flex-1"
          disabled={isSaving || !isDirty}
          onClick={onSave}>
          {isSaving ? 'Saving…' : saveLabel}
        </Button>
      </div>
    </DutyCardFooter>
  )
}

/**
 * Inline fetch-error message. Also reports the failure to the duty-detail
 * toast aggregator (`useFetchErrorToast`) on mount so a single card failure
 * surfaces a toast and ≥2 concurrent failures collapse into the combined
 * "Failed to load duty details" toast — every migrated card inherits this by
 * rendering `DutyCardError` instead of wiring the aggregator itself.
 */
export function DutyCardError({
  title,
  children,
}: {
  /** Card name used in the aggregated toast, e.g. "Connection". */
  title: string
  children: ReactNode
}) {
  const { reportFetchError, reset } = useFetchErrorToast()
  useMountEffect(() => {
    reportFetchError(title)
    return reset
  })
  return <p className="text-xs text-destructive">{children}</p>
}

/**
 * Loading placeholder for a card body or section. Inline by default; `block`
 * reserves body height and centres, for an expanded card that is about to fill
 * with content and would otherwise render one line and then jump.
 */
export function DutyCardLoading({
  label = 'Loading…',
  block = false,
}: {
  label?: string
  block?: boolean
}) {
  const text = <span className="text-sm text-muted-foreground">{label}</span>
  if (!block) return text
  return <div className="flex h-32 items-center justify-center">{text}</div>
}

/**
 * Centered empty state: an optional leading icon (rendered at 32px), a message,
 * and optional action(s) (e.g. a "Generate" CTA) passed as children.
 */
export function DutyCardEmpty({
  icon,
  message,
  children,
}: {
  icon?: ReactNode
  message: ReactNode
  children?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-3 py-8 text-center">
      {icon && (
        <span className="text-muted-foreground [&_svg]:size-8">{icon}</span>
      )}
      <p className="text-sm text-muted-foreground">{message}</p>
      {children}
    </div>
  )
}
